import { useMemo } from "react";
import { useSelector } from "react-redux";
import { DisruptionOffersSelectors } from "@b2bportal/core-disruption";
import { FintechProductInfoLinksLayout } from "@b2bportal/core-fintech-ui";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import {
  DisruptionOfferQuoteSelectionEnum,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { DialogTrigger } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ClientName } from "@hopper-b2b/types";
import { getEnvVariables } from "@hopper-b2b/utilities";
import { DisruptionOfferSection } from "@tenant/disruption";
import clsx from "clsx";
import defaultStyles from "./DisruptionOfferPage.module.scss";

interface DisruptionOfferPageProps extends PlatformComponentProps {
  goToNextStep?: (replace?: boolean) => void;
  displayExternalLinksAsModal?: boolean;
}

// Standalone disruption offer screen based on core-disruption module. Used in mobile web.
export const DisruptionOfferPage = ({
  className,
  goToNextStep,
  displayExternalLinksAsModal = true,
}: DisruptionOfferPageProps) => {
  const COMPONENT_KEY = "DisruptionOfferPage";
  const [block, cn] = useModuleBEM(defaultStyles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();

  const disruptionOffer = useSelector(
    DisruptionOffersSelectors.getDisruptionOfferToShow
  );

  const selectedDisruptionOffer = useSelector(
    DisruptionOffersSelectors.getSelectedDisruptionOffer
  );

  const continueButtonText = useMemo(() => {
    if (
      selectedDisruptionOffer?.selection ===
      DisruptionOfferQuoteSelectionEnum.Selected
    ) {
      return t("core-disruption.offerCTA.continueWithSelectedOffer");
    } else {
      return t("core-disruption.offerCTA.continueWithoutSelection");
    }
  }, [t, selectedDisruptionOffer]);

  const continueButton = useMemo(
    () => ({
      goToNextStep: goToNextStep,
      Component: (props) => (
        <div
          className={clsx(
            cn("secondary-content", {
              "has-selection": props.hasSelection,
            }),
            props.className
          )}
        >
          <button
            className={cn("continue-button")}
            onClick={props.buttonClicked}
          >
            {continueButtonText}
          </button>
        </div>
      ),
    }),
    [cn, continueButtonText, goToNextStep]
  );

  if (disruptionOffer == null) {
    return null;
  }

  const bulletLinePrefixIcon =
    getEnvVariables("clientName") === ClientName.NUBANK
      ? {
          iconName: IconNameEnum.checkCircle,
          className: cn("check-icon"),
        }
      : undefined;

  const howItWorksDialogBulletLinePrefixIconName =
    getEnvVariables("clientName") === ClientName.NUBANK
      ? IconNameEnum.checkCircle
      : undefined;

  const infoLinksUI =
    getEnvVariables("clientName") === ClientName.NUBANK
      ? {
          layout: FintechProductInfoLinksLayout.Vertical,
          prefixIcon: {
            iconName: IconNameEnum.rightArrow,
            className: cn("right-arrow-icon"),
          },
        }
      : undefined;

  return (
    <div className={clsx(block, className)}>
      <div
        className={cn("container", {
          "show-secondary-content": selectedDisruptionOffer != null,
        })}
      >
        <DisruptionOfferSection
          onInvalidOffer={goToNextStep}
          disruptionOffer={disruptionOffer}
          infoLinksUI={infoLinksUI}
          optional={
            getEnvVariables("clientName") === ClientName.NUBANK && {
              centerBulletIcons: true,
              extraBodyLines: [
                {
                  lineType: "disclaimer",
                  tKey: "core-disruption.disclaimer",
                  linkUrl:
                    "https://hts.hopper.com/legal/pt-br-hts-passenger-rights-overview",
                  components: [
                    <DialogTrigger
                      className={cn("benefits-from-airline-link")}
                    ></DialogTrigger>,
                  ],
                },
              ],
            }
          }
          bulletLinePrefixIcon={bulletLinePrefixIcon}
          howItWorksDialogBulletLinePrefixIconName={
            howItWorksDialogBulletLinePrefixIconName
          }
          displayExternalLinksAsModal={displayExternalLinksAsModal}
          continueButton={continueButton}
        />
      </div>
    </div>
  );
};
